import React, { useEffect, useRef, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import gsap from 'gsap'
import { Draggable } from 'gsap/all'
import { InertiaPlugin } from '@utils/gsap/InertiaPlugin'
import {
  BrandCard,
  BrandWrapper,
  BrandCardThumbnail,
  BrandCarousel,
  BrandHeader,
  BrandMain,
  BrandNav,
  BrandNavItem,
  BrandTitle,
  BrandCardWrapper
} from './index.style'
import Container from '@components/Container'
import Spacer from '@components/Spacer'
import Button from '@components/Button'
import { GatsbyImage } from 'gatsby-plugin-image'
import useGetColor from '../useGetColor'

const BrandsSlider = ({ data, backgroundColor: bgColor, textColor: tColor }) => {
  const {items, title, background} = data;
  const [backgroundColor, textColor] = useGetColor(background, bgColor, tColor)
  const [activeIndex, setActiveIndex] = useState(0)
  const [carouselAtEnd, setCarouselAtEnd] = useState(false)
  const [wrapperWidth, setWrapperWidth] = useState()
  const activeIndexRef = useRef(activeIndex)
  const $brandWrapper = useRef()
  const $brand = useRef()
  const $singleCard = useRef()
  const slideWidth = useRef()

  const getSlideWidth = useCallback(() => {
    const cardWidth = parseFloat(
      window.getComputedStyle($singleCard.current).width.replace(/px$/, '')
    )
    const cardMargin = parseFloat(
      window.getComputedStyle($singleCard.current).marginLeft.replace(/px$/, '')
    )

    setWrapperWidth($brandWrapper.current.offsetWidth);
    return cardWidth + cardMargin
  }, [])

  const getMaxWidth = () =>
    -$brand.current.offsetWidth + $brandWrapper.current.offsetWidth

  const getCarouselX = useCallback(targetX => {
    const maxWidth = getMaxWidth()
    const isEnd = targetX < maxWidth
    const isStart = targetX > 0
    const x = isEnd ? maxWidth : isStart ? 0 : targetX

    setCarouselAtEnd(isEnd)
    return x
  }, [])

  const goToCard = useCallback(
    index => {
      gsap.to($brand.current, {
        x: getCarouselX(-(slideWidth.current * index)),
        ease: 'inOut',
        duration: 0.3,
        onComplete: () => {
          setActiveIndex(index)
        },
      })
    },
    [getCarouselX]
  )

  useEffect(() => {
    gsap.registerPlugin(Draggable, InertiaPlugin)

    Draggable.create($brand.current, {
      type: 'x',
      edgeResistance: 1,
      inertia: true,
      snap: function (endValue) {
        const maxWidth = getMaxWidth()
        const clampedValue = gsap.utils.clamp(maxWidth, 0, endValue)

        setActiveIndex(Math.ceil(Math.abs(clampedValue / slideWidth.current)))
        setCarouselAtEnd(clampedValue <= maxWidth)
        return clampedValue
      },
    })

    const applyDraggableBounds = () => {
      slideWidth.current = getSlideWidth()
      setWrapperWidth($brandWrapper.current.offsetWidth);

      Draggable.get($brand.current).applyBounds({
        minX: $brandWrapper.current.offsetWidth - $brand.current.offsetWidth,
        maxX: 0,
      })

      goToCard(activeIndexRef.current)
    }

    applyDraggableBounds()

    window.addEventListener('resize', applyDraggableBounds)

    return () => {
      window.removeEventListener('resize', applyDraggableBounds)
    }
  }, [getSlideWidth, goToCard])

  useEffect(() => {
    activeIndexRef.current = activeIndex
  }, [activeIndex])

  const getSlidePaginationAmount = () => {
    if (window.innerWidth > 2060) {
      return 5
    } else if (window.innerWidth > 1520) {
      return 5
    } else if (window.innerWidth > 990) {
      return 4
    } else if (window.innerWidth > 590) {
      return 3
    } else {
      return 2
    }
  }

  const goToPrevious = () => {
    goToCard(Math.max(activeIndex - getSlidePaginationAmount(), 0))
  }

  const goToNext = () => {
    goToCard(
      Math.min(activeIndex + getSlidePaginationAmount(), items.length - 1)
    )
  }

  return (
    <BrandMain color={textColor} background={backgroundColor}>
      <Container>
        <BrandWrapper ref={$brandWrapper}>
          <Spacer size={[30, 90]} />

          <BrandHeader>
            <BrandTitle>{title}</BrandTitle>

            <BrandNav backgroundColor={backgroundColor} fontColor={textColor}>
              <BrandNavItem>
                <Button
                  variant="outline"
                  onClick={goToPrevious}
                  size="large"
                  color="white"
                  disabled={activeIndex === 0}
                >
                  PREVIOUS
                </Button>
              </BrandNavItem>
              <BrandNavItem>
                <Button
                  variant="outline"
                  onClick={goToNext}
                  size="large"
                  color="white"
                  disabled={carouselAtEnd}
                >
                  NEXT
                </Button>
              </BrandNavItem>
            </BrandNav>
          </BrandHeader>

          <BrandCarousel ref={$brand} centered={(activeIndex === 0 && carouselAtEnd)}>
            {React.Children.toArray(
              items.map((item, itemIndex) => (
                <BrandCardWrapper
                  ref={itemIndex === 1 ? $singleCard : undefined}
                >
                  <BrandCard wrapperWidth={wrapperWidth}>
                    <BrandCardThumbnail>
                    <GatsbyImage
                        image={item.gatsbyImageData}
                        alt={item.description}
                        height="110px"
                        width="250px"
                    />
                    </BrandCardThumbnail>
                  </BrandCard>
                </BrandCardWrapper>
              ))
            )}
          </BrandCarousel>

          <Spacer size={[40, 98]} />
        </BrandWrapper>
      </Container>
    </BrandMain>
  )
}

BrandsSlider.propTypes = {
  items: PropTypes.array,
}

export default BrandsSlider
